import { PlanTier, STRIPE_PRICES, STRIPE_PRICES_USD } from '../types';
import { isEuropean } from './currencyService';
import { Capacitor } from '@capacitor/core';

const BRIDGE_URL = import.meta.env.VITE_BRIDGE_URL || '';

// Stripe Publishable Key (Test Mode) - Safe to expose in frontend
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51Sv3PtJibuRQpdrBUrJSX9SDXl2apF1EZ4uT0eMiyQXiiD0f29vxTqfxtlJkC78J9B68b9Pv6QGWCpykzaxEo31x00PUpE7klF';

// Single book one-time purchase prices
export const STRIPE_SINGLE_BOOK_PRICE_EUR = 'price_1SyGVFJibuRQpdrBO6Do334e';
export const STRIPE_SINGLE_BOOK_PRICE_USD = 'price_1TGkwOJibuRQpdrB7bS8kHOw';

// Mobile guard: Stripe cannot be used for digital purchases inside native apps
const isMobileApp = () => Capacitor.isNativePlatform();

interface CheckoutSessionResponse {
    success: boolean;
    sessionId?: string;
    url?: string;
    error?: string;
}

const callAuthEndpoint = async (action: string, payload: object) => {
    // Reuse the logic from authService if possible, or duplicate here for simplicity
    // Since stripeService doesn't import authService, we duplicate the fetch logic
    // but better refactor would be to have a apiService.ts
    // For now, implementing direct fetch
    if (!BRIDGE_URL) return { success: false, error: 'Backend no configurado' };
    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow',
            body: JSON.stringify({ action, ...payload })
        });
        const text = await response.text();
        return JSON.parse(text);
    } catch (e) {
        return { success: false, error: 'Error de conexión' };
    }
};

export const createCheckoutSession = async (
    userId: string,
    email: string,
    targetPlan: PlanTier,
    name?: string // Added optional name for customer creation
): Promise<CheckoutSessionResponse> => {

    // Block Stripe on native mobile — use native store billing instead
    if (isMobileApp()) {
        return { success: false, error: 'En la app móvil, usa la suscripción de Google Play.' };
    }

    if (targetPlan === 'free') {
        return { success: false, error: 'El plan gratuito no requiere pago' };
    }

    const priceTable = isEuropean() ? STRIPE_PRICES : STRIPE_PRICES_USD;
    const priceId = priceTable[targetPlan as keyof typeof priceTable];

    if (!BRIDGE_URL) {
        // Development mode: simulate checkout
        console.warn('Stripe: No backend configured, simulating checkout');
        return {
            success: true,
            sessionId: 'dev_session_' + Date.now(),
            url: `https://checkout.stripe.com/pay/dev_session_${Date.now()}`
        };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow', // GAS redirects, we need to follow
            body: JSON.stringify({
                action: 'createCheckoutSession',
                userId,
                email,
                name, // Pass name to GAS
                priceId,
                successUrl: window.location.origin + '?payment=success&plan=' + targetPlan,
                cancelUrl: window.location.origin + '?payment=cancelled'
            })
        });

        const text = await response.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch {
            console.error('Failed to parse Stripe response:', text);
            return { success: false, error: 'Respuesta inválida del servidor de pagos' };
        }

        if (data.success && data.url) {
            return { success: true, sessionId: data.sessionId, url: data.url };
        }

        return { success: false, error: data.error || 'Error creating checkout session' };
    } catch (error) {
        console.error('Stripe checkout error:', error);
        return { success: false, error: 'Error de conexión con el servidor de pagos' };
    }
};

/**
 * Create a one-time checkout session for single book purchase (19€)
 * Used for guest users who want to buy just one book instead of subscribing
 */
export const createSingleBookCheckout = async (
    email: string | undefined | null,
    guestId: string,
    projectId: string,
    name?: string
): Promise<CheckoutSessionResponse> => {
    // Block Stripe on native mobile
    if (isMobileApp()) {
        return { success: false, error: 'En la app móvil, usa la compra de Google Play.' };
    }
    if (!BRIDGE_URL) {
        // Development mode: simulate checkout
        console.warn('Stripe: No backend configured, simulating single book checkout');
        return {
            success: true,
            sessionId: 'dev_single_book_' + Date.now(),
            url: `https://checkout.stripe.com/pay/dev_single_book_${Date.now()}`
        };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow',
            body: JSON.stringify({
                action: 'createSingleBookCheckout',
                email,
                name,
                guestId,
                projectId,
                priceId: isEuropean() ? STRIPE_SINGLE_BOOK_PRICE_EUR : STRIPE_SINGLE_BOOK_PRICE_USD,
                successUrl: window.location.origin + '?payment=success&plan=single_book&guestId=' + guestId + '&session_id={CHECKOUT_SESSION_ID}',
                cancelUrl: window.location.origin + '?payment=cancelled'
            })
        });

        const text = await response.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch {
            console.error('Failed to parse Stripe response:', text);
            return { success: false, error: 'Respuesta inválida del servidor de pagos' };
        }

        if (data.success && data.url) {
            return { success: true, sessionId: data.sessionId, url: data.url };
        }

        return { success: false, error: data.error || 'Error creating single book checkout' };
    } catch (error) {
        console.error('Single book checkout error:', error);
        return { success: false, error: 'Error de conexión con el servidor de pagos' };
    }
};

export const cancelUserSubscription = async (userId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    if (!BRIDGE_URL) {
        return { success: true, message: 'Simulación: Suscripción cancelada (Dev Mode)' };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow',
            body: JSON.stringify({
                action: 'cancelSubscription',
                userId
            })
        });

        const text = await response.text();
        const data = JSON.parse(text);

        if (data.success) {
            return { success: true, message: data.message };
        } else {
            return { success: false, error: data.error || 'Error al cancelar la suscripción' };
        }
    } catch (error) {
        console.error('Cancellation error:', error);
        return { success: false, error: 'Error de conexión al cancelar' };
    }
};

export interface SubscriptionStatus {
    status: 'active' | 'canceled' | 'past_due' | 'expired' | 'none';
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | number | null; // Allow number for timestamps
    planName: string;
    _debugRaw?: any; // Temporary debug field
}

export const getSubscriptionStatus = async (userId: string): Promise<{ success: boolean; data?: SubscriptionStatus; error?: string }> => {
    if (!BRIDGE_URL) {
        return { success: true, data: { status: 'none', cancelAtPeriodEnd: false, currentPeriodEnd: null, planName: 'Dev Mode' } };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow',
            body: JSON.stringify({
                action: 'getSubscriptionStatus',
                userId
            })
        });

        const text = await response.text();
        console.log('Backend raw response:', text); // DEBUG LOG

        const data = JSON.parse(text);
        console.log('Parsed subscription data:', data); // DEBUG LOG

        if (data.success !== false) {
            return {
                success: true,
                data: {
                    status: data.status,
                    cancelAtPeriodEnd: data.cancelAtPeriodEnd ?? data.cancel_at_period_end,
                    currentPeriodEnd: data.currentPeriodEnd
                        ?? data.current_period_end
                        ?? data.stripeRaw?.current_period_end
                        ?? data.stripeRaw?.items?.data?.[0]?.current_period_end, // Deep fallback
                    planName: data.planName ?? data.plan_name,
                    _debugRaw: data // Include raw data for visibility
                }
            };
        } else {
            console.error('Backend returned error:', data.error); // DEBUG LOG
            return { success: false, error: data.error || 'Error al obtener estado' };
        }
    } catch (error) {
        console.error('Get subscription status error:', error);
        return { success: false, error: 'Error de conexión' };
    }
};

export const reactivateSubscription = async (userId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    if (!BRIDGE_URL) {
        return { success: true, message: 'Simulación: Suscripción reactivada (Dev Mode)' };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow',
            body: JSON.stringify({
                action: 'reactivateSubscription',
                userId
            })
        });

        const text = await response.text();
        const data = JSON.parse(text);

        if (data.success) {
            return { success: true, message: data.message };
        } else {
            return { success: false, error: data.error || 'Error al reactivar' };
        }
    } catch (error) {
        console.error('Reactivation error:', error);
        return { success: false, error: 'Error de conexión al reactivar' };
    }
};

export const verifyPayment = async (userId: string, sessionId: string): Promise<{ success: boolean; customerId?: string; error?: string }> => {
    if (!BRIDGE_URL) {
        // Dev Mode Simulation
        return { success: true, customerId: 'cus_simulated_' + Math.random().toString(36).substr(2, 9) };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow',
            body: JSON.stringify({
                action: 'verifyPayment',
                userId,
                sessionId
            })
        });

        const text = await response.text();
        const data = JSON.parse(text);

        if (data.success) {
            return { success: true, customerId: data.customerId };
        } else {
            return { success: false, error: data.error || 'Error verificando pago' };
        }
    } catch (error) {
        console.error('Verification error:', error);
        return { success: false, error: 'Error de conexión al verificar' };
    }
};

export const verifySingleBookPayment = async (userId: string, sessionId: string): Promise<{ success: boolean; plan?: PlanTier; booksRemaining?: number; error?: string }> => {
    if (!BRIDGE_URL) {
        // Dev Mode
        return { success: true, plan: 'single_book', booksRemaining: 1 };
    }

    try {
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow', // GAS redirects, we need to follow
            body: JSON.stringify({
                action: 'verifySingleBookPayment',
                userId,
                sessionId
            })
        });
        const text = await response.text();
        const data = JSON.parse(text);
        return data;
    } catch (error) {
        console.error('Verify single book error:', error);
        return { success: false, error: 'Error de conexión' };
    }
};

export const redirectToCheckout = (checkoutUrl: string): void => {
    window.location.href = checkoutUrl;
};

export const handlePaymentCallback = (): {
    isPaymentCallback: boolean;
    success: boolean;
    plan?: PlanTier;
    sessionId?: string;
} => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const plan = urlParams.get('plan') as PlanTier | null;
    const sessionId = urlParams.get('session_id');

    if (!paymentStatus) {
        return { isPaymentCallback: false, success: false };
    }

    // Clean URL after reading
    window.history.replaceState({}, document.title, window.location.pathname);

    if (paymentStatus === 'success' && plan) {
        return { isPaymentCallback: true, success: true, plan, sessionId: sessionId || undefined };
    }

    return { isPaymentCallback: true, success: false };
};

// For future: Direct Stripe.js integration
export const loadStripe = async () => {
    if (!window.Stripe) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        document.head.appendChild(script);

        await new Promise<void>((resolve) => {
            script.onload = () => resolve();
        });
    }

    return window.Stripe?.(STRIPE_PUBLISHABLE_KEY);
};

// Type augmentation for Stripe
declare global {
    interface Window {
        Stripe?: (key: string) => any;
    }
}
